export type PaymentAmountLimitMode = "amount" | "percent" | null;

export interface ResolvedPaymentAmountLimits {
  min: number;
  max: number;
  mode: PaymentAmountLimitMode;
  isConfigured: boolean;
}

export type ScheduleAmountValidationCode =
  | "LIMITS_UNAVAILABLE"
  | "INVALID_AMOUNT"
  | "OUTSIDE_LIMITS"
  | "OFFER_SCHEDULE_MISMATCH";

export interface ScheduleAmountValidationResult {
  isValid: boolean;
  code: ScheduleAmountValidationCode | null;
  limits: ResolvedPaymentAmountLimits;
}

const FALLBACK_LIMITS: ResolvedPaymentAmountLimits = {
  min: 1,
  max: 100000,
  mode: null,
  isConfigured: false,
};

const normalizeNumber = (value: unknown): number => {
  const normalizedValue = String(value ?? "")
    .replace(/[$,]/g, "")
    .trim();
  return normalizedValue === "" ? Number.NaN : Number(normalizedValue);
};

const findAmountSettings = (settings: any): any | null => {
  const candidates = [settings?.systemSettings, settings];

  return (
    candidates.find((candidate) => {
      const flag = `${candidate?.min_amount_flag || ""}`.toUpperCase();
      return flag === "A" || flag === "P";
    }) || null
  );
};

/**
 * Accepts either the complete general-settings response or its historical
 * systemSettings child. The API has returned both shapes over time.
 */
export const resolvePaymentAmountLimits = (
  settings: any,
  balance: unknown
): ResolvedPaymentAmountLimits => {
  const amountSettings = findAmountSettings(settings);
  if (!amountSettings) return { ...FALLBACK_LIMITS };

  const flag = `${amountSettings.min_amount_flag}`.toUpperCase();
  const configuredMin = normalizeNumber(amountSettings.min_amount);
  const configuredMax = normalizeNumber(amountSettings.maximum_amount);
  const normalizedBalance = normalizeNumber(balance);

  if (
    !Number.isFinite(configuredMin) ||
    !Number.isFinite(configuredMax) ||
    configuredMin < 0 ||
    configuredMax <= 0
  ) {
    return { ...FALLBACK_LIMITS };
  }

  const min =
    flag === "P" && Number.isFinite(normalizedBalance)
      ? configuredMin * normalizedBalance * 0.01
      : flag === "A"
        ? configuredMin
        : Number.NaN;

  if (!Number.isFinite(min) || min > configuredMax) {
    return { ...FALLBACK_LIMITS };
  }

  return {
    min,
    max: configuredMax,
    mode: flag === "P" ? "percent" : "amount",
    isConfigured: true,
  };
};

export const validateScheduleAmounts = ({
  settings,
  balance,
  amounts,
  expectedTotal,
  expectedCount,
}: {
  settings: any;
  balance: unknown;
  amounts: unknown[];
  expectedTotal?: unknown;
  expectedCount?: unknown;
}): ScheduleAmountValidationResult => {
  const limits = resolvePaymentAmountLimits(settings, balance);
  if (!limits.isConfigured) {
    return { isValid: false, code: "LIMITS_UNAVAILABLE", limits };
  }

  const normalizedAmounts = amounts.map(normalizeNumber);
  if (
    normalizedAmounts.length === 0 ||
    normalizedAmounts.some((amount) => !Number.isFinite(amount) || amount <= 0)
  ) {
    return { isValid: false, code: "INVALID_AMOUNT", limits };
  }

  if (
    normalizedAmounts.some(
      (amount) => amount < limits.min || amount > limits.max
    )
  ) {
    return { isValid: false, code: "OUTSIDE_LIMITS", limits };
  }

  const normalizedExpectedCount = normalizeNumber(expectedCount);
  const normalizedExpectedTotal = normalizeNumber(expectedTotal);
  const hasExpectedCount =
    expectedCount !== undefined && Number.isFinite(normalizedExpectedCount);
  const hasExpectedTotal =
    expectedTotal !== undefined && Number.isFinite(normalizedExpectedTotal);
  const actualTotal = normalizedAmounts.reduce(
    (sum, amount) => sum + amount,
    0
  );

  if (
    (hasExpectedCount &&
      normalizedAmounts.length !== normalizedExpectedCount) ||
    (hasExpectedTotal && Math.abs(actualTotal - normalizedExpectedTotal) > 0.01)
  ) {
    return {
      isValid: false,
      code: "OFFER_SCHEDULE_MISMATCH",
      limits,
    };
  }

  return { isValid: true, code: null, limits };
};
